import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ModelWithFilesAndLinks } from '@maker-toolchest/shared';
import { Box, File, Link as LinkIcon, Folder, Star, Trash2, MoreVertical, Eye } from 'lucide-react';
import { ModelFilesModal } from './ModelFilesModal';
import { ModelLinksModal } from './ModelLinksModal';
import { ModelCollectionsModal } from './ModelCollectionsModal';
import { Button } from '@/components/Button';
import { DropdownMenu } from '@/components/DropdownMenu';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useDeleteModel, useUpdateModel } from '../hooks';

interface ModelTableRowProps {
  model: ModelWithFilesAndLinks;
}

export function ModelTableRow({ model }: ModelTableRowProps) {
  const navigate = useNavigate();
  const [showFilesModal, setShowFilesModal] = useState(false);
  const [showLinksModal, setShowLinksModal] = useState(false);
  const [showCollectionsModal, setShowCollectionsModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ModelWithFilesAndLinks | null>(null);

  const deleteMutation = useDeleteModel();
  const updateMutation = useUpdateModel();

  const handleRowClick = () => {
    navigate(`/models/${model.id}`);
  };

  const handleBadgeClick = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation(); // Prevent row navigation
    action();
  };

  const handleToggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row navigation
    try {
      await updateMutation.mutateAsync({
        id: model.id,
        input: { isFavorite: !model.isFavorite },
      });
    } catch {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  // Thumbnail logic
  const thumbnail = model.thumbnailUrl;

  return (
    <>
      <tr
        onClick={handleRowClick}
        className="border-b border-border hover:bg-background-subtle transition-colors cursor-pointer"
      >
        {/* Thumbnail */}
        <td className="p-3">
          <div className="flex items-center justify-center bg-background-subtle h-12 w-12 rounded overflow-hidden flex-shrink-0">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={model.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <Box className="h-6 w-6 text-foreground-muted" />
            )}
          </div>
        </td>

        {/* Name, Description & Collections */}
        <td className="p-3">
          <div className="space-y-1">
            <div className="text-sm text-foreground">{model.name}</div>
            {model.description && (
              <div className="text-xs text-foreground-muted line-clamp-2">
                {model.description}
              </div>
            )}
            {/* Collections badges below name */}
            {model.collections && model.collections.length > 0 && (
              <div className="flex flex-wrap gap-1 pt-1">
                {model.collections.length === 1 ? (
                  <button
                    onClick={(e) => handleBadgeClick(e, () => setShowCollectionsModal(true))}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded border transition-opacity hover:opacity-80"
                    style={{
                      borderColor: model.collections[0].collection.color || '#e5e7eb',
                      color: model.collections[0].collection.color || '#6b7280',
                      backgroundColor: model.collections[0].collection.color
                        ? `${model.collections[0].collection.color}10`
                        : '#f3f4f6',
                    }}
                  >
                    <Folder className="h-3 w-3" />
                    <span>{model.collections[0].collection.name}</span>
                  </button>
                ) : (
                  <button
                    onClick={(e) => handleBadgeClick(e, () => setShowCollectionsModal(true))}
                    className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded bg-gray-100 text-gray-800 dark:bg-gray-800/30 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800/40 transition-colors"
                  >
                    <Folder className="h-3 w-3" />
                    <span>{model.collections.length}</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </td>

        {/* Files */}
        <td className="p-3">
          {model.files.length > 0 && (
            <button
              onClick={(e) => handleBadgeClick(e, () => setShowFilesModal(true))}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
            >
              <File className="h-3 w-3" />
              <span>
                {model.files.length} {model.files.length === 1 ? 'file' : 'files'}
              </span>
            </button>
          )}
        </td>

        {/* Links */}
        <td className="p-3">
          {model.links.length > 0 && (
            <button
              onClick={(e) => handleBadgeClick(e, () => setShowLinksModal(true))}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300 hover:bg-blue-200 dark:hover:bg-blue-900/40 transition-colors"
            >
              <LinkIcon className="h-3 w-3" />
              <span>
                {model.links.length} {model.links.length === 1 ? 'link' : 'links'}
              </span>
            </button>
          )}
        </td>

        {/* Actions */}
        <td className="p-3 text-right">
          <div className="flex items-center justify-end gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleToggleFavorite}
              title={model.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              className={model.isFavorite ? 'text-warning' : ''}
            >
              <Star className={`h-4 w-4 ${model.isFavorite ? 'fill-current' : ''}`} />
            </Button>
            <DropdownMenu
              trigger={
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => e.stopPropagation()}
                  title="More actions"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              }
              items={[
                {
                  label: 'View Model',
                  icon: <Eye className="h-4 w-4" />,
                  onClick: () => navigate(`/models/${model.id}`),
                },
                {
                  label: 'Delete',
                  icon: <Trash2 className="h-4 w-4" />,
                  onClick: () => setDeleteTarget(model),
                  variant: 'danger' as const,
                },
              ]}
            />
          </div>
        </td>
      </tr>

      {/* Modals */}
      <ModelFilesModal
        open={showFilesModal}
        onClose={() => setShowFilesModal(false)}
        modelId={model.id}
        modelName={model.name}
        files={model.files}
      />
      <ModelLinksModal
        open={showLinksModal}
        onClose={() => setShowLinksModal(false)}
        modelName={model.name}
        links={model.links}
      />
      <ModelCollectionsModal
        open={showCollectionsModal}
        onClose={() => setShowCollectionsModal(false)}
        modelName={model.name}
        collections={model.collections || []}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Model"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This will permanently delete the model and all its files.`}
        confirmText="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </>
  );
}
